/**
 * Goli-style bundle order cards where each pack tier is its own bundle
 * product (own price, compare-at price, image and optional selling plans).
 *
 * - Group tabs bring the selected group's cards to the front (CSS order).
 * - Pack pills switch the active tier panel; the active panel owns the
 *   image, discount badge and price boxes.
 * - Subscribe mode reads real selling plans (subscription app) and adds
 *   the line with selling_plan.
 * - Add to cart goes through Ajax and keeps the cart drawer in sync.
 *   Native form POST still works without JS for the first tier (one-time).
 */
if (!customElements.get('bundle-order')) {
  class BundleOrder extends HTMLElement {
    connectedCallback() {
      this.animateTabs = this.closest('.bundle-order')?.dataset.animateTabs !== 'false';
      this.cartDrawer = document.querySelector('cart-drawer');
      this.bar = this.querySelector('[data-bar]');
      this.barCount = this.querySelector('[data-bar-count]');
      this.forms = this.querySelectorAll('[data-card-form]');

      this.forms.forEach((form) => {
        form.addEventListener('submit', (event) => this.#onSubmit(event));

        form.querySelectorAll('.bundle-order__pack-input').forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            this.#normalize(form, radio.dataset.tierIndex, form.dataset.mode, form.dataset.defaultPlan || 0);
          });
        });

        form.querySelectorAll('.bundle-order__opt-input--sub').forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            form.dataset.mode = 'subscribe';
            this.#normalize(form, this.#activeTier(form), 'subscribe', form.dataset.defaultPlan || 0);
          });
        });

        form.querySelectorAll('.bundle-order__opt-input[value="onetime"]').forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            form.dataset.mode = 'onetime';
            this.#normalize(form, this.#activeTier(form), 'onetime', 0);
          });
        });

        form.querySelectorAll('.bundle-order__sp-input').forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            form.dataset.mode = 'subscribe';
            this.#normalize(form, this.#activeTier(form), 'subscribe', radio.dataset.planIndex);
          });
        });

        this.#normalize(form, form.dataset.defaultTier || 0, form.dataset.defaultMode, form.dataset.defaultPlan || 0);

        // Make the whole purchase box a click target, not just its title row.
        form.querySelectorAll('.bundle-order__opt').forEach((box) => {
          box.addEventListener('click', (event) => {
            if (event.target.closest('label') || event.target.closest('input')) return;
            const radio = box.querySelector('.bundle-order__opt-input');
            if (!radio || radio.checked) return;
            radio.checked = true;
            const mode = radio.value === 'subscribe' ? 'subscribe' : 'onetime';
            form.dataset.mode = mode;
            this.#normalize(form, this.#activeTier(form), mode, form.dataset.defaultPlan || 0);
          });
        });
      });

      this.#setupStripDrag();

      const filter = this.querySelector('[data-group-filter]');
      this.grid = this.querySelector('[data-grid]');
      if (filter && this.grid) {
        filter.addEventListener('click', (event) => {
          const tab = event.target.closest('[data-group]');
          if (!tab) return;
          const group = tab.dataset.group;
          filter.querySelectorAll('[data-group]').forEach((t) => {
            const active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-pressed', String(active));
          });
          // Goli behaviour: bring the selected group's cards to the front
          // instead of hiding the rest.
          const applyOrdering = () => {
            const ordering = group !== '*';
            this.grid.classList.toggle('has-active', ordering);
            this.querySelectorAll('.bundle-order__item[data-group]').forEach((item) => {
              item.classList.toggle('is-active-group', ordering && item.dataset.group === group);
            });
          };
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (this.animateTabs && document.startViewTransition && !reducedMotion) {
            document.startViewTransition(applyOrdering);
          } else {
            applyOrdering();
          }
        });
      }
    }

    /**
     * Mouse/pen drag-to-scroll for the group tab strip. Touch keeps the
     * browser's native horizontal panning.
     */
    #setupStripDrag() {
      const strip = this.querySelector('.bundle-order__strip');
      if (!strip) return;

      let down = false;
      let dragged = false;
      let startX = 0;
      let startLeft = 0;

      strip.addEventListener('dragstart', (event) => event.preventDefault());
      strip.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        down = true;
        dragged = false;
        startX = event.clientX;
        startLeft = strip.scrollLeft;
      });
      strip.addEventListener('pointermove', (event) => {
        if (!down) return;
        const dx = event.clientX - startX;
        if (!dragged && Math.abs(dx) < 5) return;
        dragged = true;
        strip.scrollLeft = startLeft - dx;
        event.preventDefault();
      });
      const stopDrag = () => {
        down = false;
      };
      strip.addEventListener('pointerup', stopDrag);
      strip.addEventListener('pointercancel', stopDrag);
      strip.addEventListener('pointerleave', stopDrag);
      // Suppress the click that follows a drag so tabs don't switch by accident.
      strip.addEventListener(
        'click',
        (event) => {
          if (!dragged) return;
          dragged = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    }

    #activeTier(form) {
      const pack = form.querySelector('.bundle-order__pack-input:checked');
      return pack ? pack.dataset.tierIndex : '0';
    }

    /**
     * Enforces a single consistent selection per form:
     * one active tier panel, one purchase mode radio, one selling plan
     * radio, the hidden product id, the visible price line and badge.
     */
    #normalize(form, tierIndex, mode, planIndex) {
      const panel = form.querySelector(`.bundle-order__tier-panel[data-tier-panel="${tierIndex}"]`);
      if (!panel) return;

      form.querySelectorAll('[data-tier-input]').forEach((radio) => {
        radio.checked = false;
      });

      const pack = form.querySelector(`.bundle-order__pack-input[data-tier-index="${tierIndex}"]`);
      const mainId = form.querySelector('[data-main-id]');
      if (pack && pack.dataset.variantId && mainId) {
        mainId.value = pack.dataset.variantId;
      }

      if (mode === 'subscribe') {
        const subRadio = panel.querySelector('.bundle-order__opt-input--sub');
        if (subRadio) subRadio.checked = true;
        const sps = panel.querySelectorAll('.bundle-order__sp-input');
        if (sps.length > 0) {
          const idx = Math.min(Math.max(parseInt(planIndex, 10) || 0, 0), sps.length - 1);
          sps[idx].checked = true;
        } else {
          form.dataset.mode = 'onetime';
        }
      } else {
        const oneRadio = panel.querySelector('.bundle-order__opt-input[value="onetime"]');
        if (oneRadio) oneRadio.checked = true;
      }

      const spChecked = panel.querySelector('.bundle-order__sp-input:checked');
      const activePlan = spChecked ? spChecked.dataset.planIndex : null;
      // Keep one plan price visible even in one-time mode so the subscribe
      // box always reads as a complete offer. Fall back to the first price
      // line that actually exists: when a single cycle is rendered from an
      // offset its index is not 0.
      const priceSpans = Array.from(panel.querySelectorAll('[data-plan-price]'));
      const shownPlan =
        activePlan !== null ? activePlan : (priceSpans[0] ? priceSpans[0].dataset.planPrice : null);
      priceSpans.forEach((el) => {
        el.hidden = shownPlan === null || el.dataset.planPrice !== shownPlan;
      });
    }

    async #onSubmit(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const button = form.querySelector('[type="submit"]');
      const errorEl = form.querySelector('[data-error]');

      if (button.getAttribute('aria-disabled') === 'true') return;
      button.setAttribute('aria-disabled', 'true');
      button.classList.remove('is-success');
      errorEl.hidden = true;

      const id = form.elements.id.value;
      const item = { id, quantity: 1 };
      const sellingPlan = form.elements.selling_plan;
      if (sellingPlan && sellingPlan.value) {
        item.selling_plan = sellingPlan.value;
      }

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [item],
            sections,
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (data.status) {
          const err = new Error(data.description || data.message);
          err.fromCartResponse = true;
          throw err;
        }

        this.#updateCartIcon(data);

        if (theme.settings.afterAtc === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }

        if (this.cartDrawer) {
          this.cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        }

        button.classList.add('is-success');

        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!cartResponse.ok) throw new Error(cartResponse.status);
        const cart = await cartResponse.json();
        this.#updateBar(cart.item_count);

        this.dispatchEvent(
          new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { cart }
          })
        );
      } catch (error) {
        console.log(error); // eslint-disable-line
        errorEl.textContent = error.fromCartResponse
          ? error.message
          : (theme.strings.cartError || 'Something went wrong. Please try again.');
        errorEl.hidden = false;
      } finally {
        button.removeAttribute('aria-disabled');
      }
    }

    #updateCartIcon(data) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections && data.sections['cart-icon-bubble']) {
        bubble.innerHTML = data.sections['cart-icon-bubble'];
      }
    }

    #updateBar(count) {
      if (!this.bar || !this.barCount) return;
      this.barCount.textContent = count;
      this.bar.hidden = count === 0;
    }
  }

  customElements.define('bundle-order', BundleOrder);
}
